import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// The Ask run is a multi-step reasoning loop that takes a while. Rather than a
// bare spinner, walk the leader through what the analyst is actually doing.
const STAGES: { label: string; note: string; at: number }[] = [
  { label: 'Reading the question', note: 'Working out what would actually answer it', at: 0 },
  { label: 'Pulling the signals', note: 'Pulse, anchors and participation, k≥5 only', at: 6 },
  { label: 'Cutting by team and segment', note: 'Finding where the movement really sits', at: 16 },
  { label: 'Listening to conversations', note: 'Themes people raised in their own words', at: 30 },
  { label: 'Searching the knowledge base', note: 'House methodology and your documents', at: 46 },
  { label: 'Weighing the evidence', note: 'Separating firm findings from hypotheses', at: 62 },
  { label: 'Writing the brief', note: 'Situation, drivers, and what to do next', at: 80 },
];

const LONG_WAIT_NOTE = 'Still reasoning. Deeper questions take a little longer.';

export function AskProgress({ className }: { className?: string }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  let activeIndex = 0;
  STAGES.forEach((s, i) => {
    if (elapsed >= s.at) activeIndex = i;
  });

  const pct = Math.min(96, 4 + (elapsed / 100) * 92);

  return (
    <div
      className={cn('rounded-md border border-border bg-muted/30 p-4 space-y-4', className)}
      role="status"
      aria-live="polite"
      aria-label="Ask is analysing"
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium">Working through the evidence</p>
        <span className="text-xs tabular-nums text-muted-foreground">
          {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
        </span>
      </div>

      <div className="h-1 w-full overflow-hidden rounded-full bg-border">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-1000 ease-linear"
          style={{ width: `${pct}%` }}
        />
      </div>

      <ol className="space-y-2">
        {STAGES.map((s, i) => {
          const done = i < activeIndex;
          const active = i === activeIndex;
          if (i > activeIndex) return null;
          return (
            <li
              key={s.label}
              className={cn(
                'flex items-start gap-2.5 animate-fade-in',
                done && 'opacity-55',
              )}
            >
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                {done ? (
                  <Check className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                )}
              </span>
              <span className="min-w-0">
                <span className={cn('block text-sm', active && 'font-medium')}>{s.label}</span>
                {active && (
                  <span className="block text-xs text-muted-foreground">{s.note}</span>
                )}
              </span>
            </li>
          );
        })}
      </ol>

      {elapsed > 100 && (
        <p className="text-xs text-muted-foreground animate-fade-in">{LONG_WAIT_NOTE}</p>
      )}
    </div>
  );
}
